import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, SendHorizonal, Sparkles } from "lucide-react";
import { SarvaMark } from "./SarvaMark";
import { API } from "@/lib/api";

const QUICK = ["What is BRSR?", "What is Scope 3?", "How do I start ESG?", "Which ESG service do I need?"];

// Model selector — ids must match the backend whitelist
const MODELS = [
  { id: "gemini-pro", label: "Gemini 3.1 Pro" },
  { id: "gemini-flash", label: "Gemini Flash" },
  { id: "gpt", label: "GPT-5.4" },
];

export default function FloatingAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello — I'm the Ask SarvaRitam assistant. I can answer general questions about ESG, BRSR, carbon accounting, climate risk and more. What would you like to understand?",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [model, setModel] = useState(() => localStorage.getItem("sr_model") || "gemini-pro");

  const pickModel = (id) => {
    setModel(id);
    localStorage.setItem("sr_model", id);
  };
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  if (!sessionRef.current) {
    const stored = localStorage.getItem("sr_session");
    sessionRef.current = stored || Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("sr_session", sessionRef.current);
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: sessionRef.current, message: msg, model }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          const data = line.replace(/^data:\s*/, "");
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.delta };
                return copy;
              });
            }
            if (parsed.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: parsed.error };
                return copy;
              });
            }
          } catch (e) { /* partial line */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry — the assistant is temporarily unavailable. Please try again in a moment.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-24 md:bottom-7 right-4 md:right-7 z-[70] flex items-center gap-2.5 bg-gold text-charcoal pl-4 pr-5 py-3.5 rounded-full shadow-2xl shadow-gold/25 hover:scale-[1.03] active:scale-95 transition-transform"
        data-testid="assistant-launcher-button"
        aria-label={open ? "Close Ask SarvaRitam assistant" : "Open Ask SarvaRitam assistant"}
      >
        {open ? <X size={19} /> : <Sparkles size={19} />}
        <span className="font-bold text-[13.5px] tracking-wide">{open ? "Close" : "Ask SarvaRitam"}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="fixed z-[70] bottom-[92px] md:bottom-24 right-4 md:right-7 w-[calc(100vw-2rem)] max-w-[400px] bg-ink2 border rule-dark shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
            style={{ height: "min(600px, calc(100vh - 9rem))" }}
            data-testid="assistant-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b rule-dark bg-charcoal/70">
              <SarvaMark className="w-8 h-8" />
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-offwhite text-[15px] leading-none">Ask SarvaRitam</p>
                <p className="text-[10.5px] font-mono uppercase tracking-widest2 text-sage mt-1.5">AI · ESG & Sustainability, in plain language</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full border rule-dark flex items-center justify-center text-mute hover:text-gold hover:border-gold/60 transition-colors shrink-0"
                aria-label="Close assistant"
                data-testid="assistant-panel-close-button"
              >
                <X size={17} />
              </button>
            </div>

            <div className="flex items-center gap-1.5 px-4 py-2 border-b rule-dark bg-charcoal/40" data-testid="assistant-model-picker" role="radiogroup" aria-label="Assistant AI model">
              <span className="font-mono text-[9px] uppercase tracking-widest2 text-white/60 mr-1.5">Model</span>
              {MODELS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => pickModel(m.id)}
                  className={`px-3 py-2 text-[11.5px] font-bold rounded-full border transition-all ${
                    model === m.id ? "bg-gold text-charcoal border-gold" : "border-white/15 text-white/80 hover:border-gold/60 hover:text-gold"
                  }`}
                  data-testid={`assistant-model-${m.id}`}
                  role="radio"
                  aria-checked={model === m.id}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-lenis-prevent data-testid="assistant-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={m.role === "user" ? "assistant-user-message" : "assistant-bot-message"}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-[13.5px] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-gold text-charcoal font-medium rounded-2xl rounded-br-sm" : "bg-white/[0.06] text-offwhite/90 rounded-2xl rounded-bl-sm"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="assistant-quick-prompts">
                {QUICK.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    className="text-[13px] font-semibold text-sage border rule-dark px-4 py-2.5 rounded-full hover:text-gold hover:border-gold/50 transition-colors"
                    data-testid={`assistant-quick-${q.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <form
              className="p-3 border-t rule-dark flex items-center gap-2 bg-charcoal/60"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about ESG, BRSR, Scope 3…"
                className="flex-1 bg-white/[0.06] border rule-dark px-4 py-3 text-[13.5px] text-offwhite placeholder:text-white/55 focus:outline-none focus:border-gold/60 rounded-full"
                data-testid="assistant-input"
                aria-label="Ask the SarvaRitam assistant"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="w-11 h-11 rounded-full bg-gold text-charcoal flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
                data-testid="assistant-send-button"
                aria-label="Send message"
              >
                <SendHorizonal size={17} />
              </button>
            </form>
            <p className="px-4 pb-3 text-[10px] text-white/55 leading-snug">
              AI-generated general information — not advice. For guidance on your organisation, book a consultation.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
